import axios from 'axios';
import React from 'react';
import { getQueryParamValue } from 'src/utils/app';
import {
  GenerationStatusEnum,
  ContentGenerationRecord,
  GetEntityMetadataResponseRecord,
  GenerationStatus
} from './types';
import { apiGetContentGenerationRequestsByProject } from '../api/contentGenApis';
import { AppTableColumnDef } from '@stackline/ui';
import { useContentGenerationStore } from './components/ProductTable/store';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import { Status } from './components/Status';
import moment from 'moment-timezone';
import ProductInfo from './components/ProductInfo';
import { EditIcon } from 'src/components/SvgIcons/SvgIcons';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import GrayBackgroundCheckbox from './components/GrayBackgroundCheckbox';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { DEFAULT_PROFILE_NAME } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/utils';
import { getQueryParams } from 'src/utils/browser';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

export enum ColumnField {
  CHECKBOX = 'checkbox',
  PRODUCT_INFO = 'productInfo',
  RETAILER_SKU = 'retailerSku',
  STACKLINE_SKU = 'stacklineSku',
  UPDATED_AT = 'updatedAt',
  PROFILE_NAME = 'profileName',
  STATUS = 'status',
  VERSION = 'version'
}

const getInitialProductData = async ({
  variant,
  appFilters,
  pageNumber,
  pageSize,
  searchTerm,
  projectId,
  retailerSkus,
  excludeRetailerSkus,
  overrideStatuses
}: {
  variant?: 'default' | 'review' | 'edit' | 'modal';
  appFilters?: ReturnType<typeof useBeaconFilters>['appFilters'] | null;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  projectId?: string;
  retailerSkus?: string[];
  excludeRetailerSkus?: string[];
  overrideStatuses?: GenerationStatus[];
}): Promise<{ records: ContentGenerationRecord[]; totalCount: number }> => {
  const retailerId = getQueryParamValue('rid');

  // Exclude NO_GENERATION only for 'review' and 'edit' with an existing project
  const excludeNoGeneration = variant === 'review' || (variant === 'edit' && projectId != null);

  const statuses: GenerationStatus[] = overrideStatuses ?? (excludeNoGeneration
    ? [
        GenerationStatusEnum.enum.READY_TO_REVIEW,
        GenerationStatusEnum.enum.GENERATING,
        GenerationStatusEnum.enum.REVIEWED,
        GenerationStatusEnum.enum.GENERATION_ERROR
      ]
    : [
        GenerationStatusEnum.enum.NO_GENERATION,
        GenerationStatusEnum.enum.READY_TO_REVIEW,
        GenerationStatusEnum.enum.GENERATING,
        GenerationStatusEnum.enum.REVIEWED,
        GenerationStatusEnum.enum.GENERATION_ERROR
      ]
  );

  const baseBody = {
    retailerId: Number(retailerId),
    status: statuses,
    ...(pageNumber && { pageNumber }),
    ...(pageSize && { pageSize }),
    ...(searchTerm && { searchTerm }),
    ...(retailerSkus && retailerSkus.length > 0 && { retailerSkus }),
    ...(appFilters?.categoryFilters &&
      appFilters.categoryFilters.length > 0 && { categoryIds: appFilters.categoryFilters }),
    ...(appFilters?.subcategoryFilters &&
      appFilters.subcategoryFilters.length > 0 && { subcategoryIds: appFilters.subcategoryFilters }),
    ...(appFilters?.segmentFilters && { segmentIds: [appFilters.segmentFilters.id] }),
    ...(excludeRetailerSkus && excludeRetailerSkus.length > 0 && { excludeRetailerSkus: excludeRetailerSkus }),
    ...(projectId !== undefined && projectId !== null && { projectId })
  };

  const res = await apiGetContentGenerationRequestsByProject(baseBody);
  const records = res.data.records;

  const stacklineSkus = records.map((record) => record.stacklineSku);

  const { data: productMetadataResponse } = await axios.post<GetEntityMetadataResponseRecord[]>(
    '/api/beacon/GetEntityMetadataInBulk',
    {
      entityType: 'product',
      entityIds: stacklineSkus,
      retailerId
    }
  );

  const stacklineSkuToMetadataMap = productMetadataResponse.reduce((acc, record) => {
    acc.set(record.stacklineSku, record);
    return acc;
  }, new Map<string, GetEntityMetadataResponseRecord>());

  return {
    records: records.map((record) => ({
      ...record,
      updatedAt: record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '',
      stacklineSku: record.stacklineSku || '',
      profileId: record.profileId || '',
      status: record.status || GenerationStatusEnum.enum.NO_GENERATION,
      profileName: record.profileName || '',
      profileVersion: record.profileVersion,
      totalRetailSales: record.totalRetailSales || 0,
      version: record.version || 0,
      retailerSku: stacklineSkuToMetadataMap.get(record.stacklineSku)?.retailerSku || '',
      categoryId: stacklineSkuToMetadataMap.get(record.stacklineSku)?.categoryId,
      subCategoryId: stacklineSkuToMetadataMap.get(record.stacklineSku)?.subCategoryId,
      product: stacklineSkuToMetadataMap.get(record.stacklineSku),
      projectId: record.projectId
    })),
    totalCount: res.data.totalCount
  };
};

export const getProductMetadata = async (skuId: string): Promise<GetEntityMetadataResponseRecord | null> => {
  const retailerId = getQueryParamValue('rid');

  try {
    const { data: productMetadataResponse } = await axios.post<GetEntityMetadataResponseRecord[]>(
      '/api/beacon/GetEntityMetadataInBulk',
      {
        entityType: 'product',
        entityIds: [skuId],
        retailerId
      }
    );

    return productMetadataResponse[0] || null;
  } catch (error) {
    console.error('Error fetching product metadata:', error);
    return null;
  }
};

export const adaptInitialData = (data: ContentGenerationRecord[]): ContentGenerationRecord[] => {
  const { selectedItems } = useContentGenerationStore.getState();
  return data.map((item) => ({
    ...item,
    checked: selectedItems.has(item.retailerSku)
  }));
};

// Master column definitions object
const createColumnDefinitions = (
  rowData: ContentGenerationRecord[],
  pageSize: number,
  currentPage: number,
  onEditProfile?: (data: ContentGenerationRecord) => void,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void,
  variant?: 'default' | 'review' | 'edit' | 'modal'
): Record<ColumnField, Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>> => {
  // Use provided selection handlers or fall back to global store
  const store = useContentGenerationStore.getState();
  const _selectedItems = selectedItems ?? store.selectedItems;
  const _toggleItem = toggleItem ?? store.toggleItem;
  const _setSelectedItems = setSelectedItems ?? store.setSelectedItems;
  const _deselectAll = deselectAll ?? store.deselectAll;
  const currentTab = getQueryParams('tab');
  const currentSubtab = getQueryParams('subtab');
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageItems = rowData.slice(startIndex, endIndex);

  // On PROJECTS tab, allow selecting GENERATING items; otherwise filter them out
  const allKeys = currentPageItems
    .filter((item) => currentSubtab === BEACON_SUBTABS.PROJECTS_SETTINGS || item.status !== GenerationStatusEnum.enum.GENERATING)
    .map((item) => item.retailerSku);

  const isAllSelected = allKeys.length > 0 && allKeys.every((key) => _selectedItems.has(key));

  return {
    [ColumnField.CHECKBOX]: {
      field: ColumnField.CHECKBOX,
      headerName: (
        <CheckboxItem
          label=""
          value="select-all"
          checked={isAllSelected}
          onClick={() => {
            if (isAllSelected) {
              _deselectAll();
            } else {
              _setSelectedItems(new Set(allKeys));
            }
          }}
        />
      ),
      tableColumnProps: {
        align: 'left',
        sx: {
          maxWidth: '16px',
          paddingRight: '0 !important',
          boxSizing: 'content-box',
          ...(variant === 'modal' && { paddingLeft: '0px !important' })
        }
      },
      valueFormatter: (_, data: ContentGenerationRecord) => {
        const isChecked = _selectedItems.has(data.retailerSku);
        const isDisabled =
          data.status === GenerationStatusEnum.enum.GENERATING && currentTab !== BEACON_TABS.PROJECTS;

        return (
          <GrayBackgroundCheckbox
            label=""
            disabled={isDisabled}
            value={data.retailerSku}
            checked={isChecked}
            onClick={() => {
              _toggleItem(data.retailerSku);
            }}
          />
        );
      }
    },
    [ColumnField.PRODUCT_INFO]: {
      field: ColumnField.PRODUCT_INFO,
      headerName: 'PRODUCTS',
      tableColumnProps: {
        align: 'left',
        sx: {
          paddingLeft: '24px !important',
          minWidth: '400px'
        }
      },
      valueFormatter: (_, data) => <ProductInfo data={data} variant={variant === 'modal' ? 'modal' : 'default'} />
    },
    [ColumnField.RETAILER_SKU]: {
      field: ColumnField.RETAILER_SKU,
      headerName: variant === 'modal' ? 'RETAIL SKU' : 'RETAILER SKU',
      tableColumnProps: {
        align: 'left',
        sx: {
          whiteSpace: 'nowrap'
        }
      },
      valueFormatter: (_, data) => data.retailerSku || '-'
    },
    [ColumnField.STACKLINE_SKU]: {
      field: ColumnField.STACKLINE_SKU,
      headerName: 'RETAILER SKU',
      tableColumnProps: {
        align: 'left',
        sx: {
          whiteSpace: 'nowrap'
        }
      },
      valueFormatter: (_, data) => data.retailerSku || '-'
    },
    [ColumnField.UPDATED_AT]: {
      field: ColumnField.UPDATED_AT,
      headerName: 'LAST EDITED',
      tableColumnProps: {
        align: 'left',
        sx: {
          whiteSpace: 'nowrap'
        }
      },
      valueFormatter: (_, data) => {
        if (!data.updatedAt || data.updatedAt === '1/1/1, 12:00:00 AM') {
          const lastMasterContentDate = data.product?.lastModified || data.product?.createdAt;
          if (lastMasterContentDate) {
            const formattedDate = moment.utc(lastMasterContentDate).tz('America/Los_Angeles').format('MMM D, YYYY');
            return <div style={{ whiteSpace: 'nowrap' }}>{formattedDate}</div>;
          }
          return '-';
        }

        const formattedDate = moment.utc(data.updatedAt).tz('America/Los_Angeles').format('MMM D, YYYY');
        return <div style={{ whiteSpace: 'nowrap' }}>{formattedDate}</div>;
      }
    },
    [ColumnField.PROFILE_NAME]: {
      field: ColumnField.PROFILE_NAME,
      headerName: 'CONTENT PROFILE',
      tableColumnProps: {
        align: 'left'
      },
      valueFormatter: (_, data) => {
        let profileDisplayName = data.profileName;

        if (data.profileName?.includes('Dynamic Profile')) {
          const splitProfileName = data.profileName.split('-');
          profileDisplayName = splitProfileName.slice(0, 2).join('-');
        }

        const isGenerating = data.status === GenerationStatusEnum.enum.GENERATING;
        const isEditEnabled = onEditProfile && !isGenerating;

        return (
          <Flex gap={12} alignItems="center">
            <div style={{ width: '24px', height: '24px' }}>
              <EditIcon
                style={{
                  width: 24,
                  height: 24,
                  cursor: isEditEnabled ? 'pointer' : 'not-allowed',
                  opacity: isEditEnabled ? 1 : 0.3
                }}
                onClick={isEditEnabled ? () => onEditProfile(data) : undefined}
              />
            </div>

            <span
              style={{
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.4',
                maxHeight: '2.8em'
              }}
            >
              {profileDisplayName || DEFAULT_PROFILE_NAME}
            </span>
          </Flex>
        );
      }
    },
    [ColumnField.STATUS]: {
      field: ColumnField.STATUS,
      headerName: 'STATUS',
      tableColumnProps: {
        align: 'left',
        sx: {
          whiteSpace: 'nowrap'
        }
      },
      valueFormatter: (_, data) => <Status data={data} onEnableFinalReview={onEnableFinalReview} />
    },
    [ColumnField.VERSION]: {
      field: ColumnField.VERSION,
      headerName: 'VERSIONS',
      tableColumnProps: {
        align: 'left'
      },
      valueFormatter: (_, data) => data.version || 0
    }
  };
};

export const getColumnDefs = (
  rowData: ContentGenerationRecord[],
  removeCheckbox: boolean = false,
  pageSize: number,
  currentPage: number,
  onEditProfile?: (data: ContentGenerationRecord) => void,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void
): (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] => {
  const columnDefs = createColumnDefinitions(
    rowData,
    pageSize,
    currentPage,
    onEditProfile,
    selectedItems,
    toggleItem,
    setSelectedItems,
    deselectAll,
    onEnableFinalReview
  );

  const fields = removeCheckbox
    ? [
      ColumnField.PRODUCT_INFO,
      ColumnField.STACKLINE_SKU,
      ColumnField.UPDATED_AT,
      ColumnField.PROFILE_NAME,
      ColumnField.STATUS,
      ColumnField.VERSION
    ]
    : [
      ColumnField.CHECKBOX,
      ColumnField.PRODUCT_INFO,
      ColumnField.STACKLINE_SKU,
      ColumnField.UPDATED_AT,
      ColumnField.PROFILE_NAME,
      ColumnField.STATUS,
      ColumnField.VERSION
    ];

  return fields.map((field) => columnDefs[field]);
};

export const getColumnDefsV2 = (
  rowData: ContentGenerationRecord[],
  pageSize: number,
  currentPage: number,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void,
  variant?: 'default' | 'review' | 'edit' | 'modal'
): (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] => {
  const columnDefs = createColumnDefinitions(
    rowData,
    pageSize,
    currentPage,
    undefined,
    selectedItems,
    toggleItem,
    setSelectedItems,
    deselectAll,
    onEnableFinalReview,
    variant
  );

  const fields = [
    ColumnField.CHECKBOX,
    ColumnField.PRODUCT_INFO,
    ColumnField.RETAILER_SKU,
    ColumnField.UPDATED_AT,
    ColumnField.VERSION
  ];

  return fields.map((field) => columnDefs[field]);
};

export const getColumnDefsGenerate = (
  rowData: ContentGenerationRecord[],
  pageSize: number,
  currentPage: number,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void
): (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] => {
  const columnDefs = createColumnDefinitions(
    rowData,
    pageSize,
    currentPage,
    undefined,
    selectedItems,
    toggleItem,
    setSelectedItems,
    deselectAll,
    onEnableFinalReview
  );

  const fields = [
    ColumnField.CHECKBOX,
    ColumnField.PRODUCT_INFO,
    ColumnField.RETAILER_SKU,
    ColumnField.UPDATED_AT,
    ColumnField.VERSION,
    ColumnField.STATUS
  ];

  return fields.map((field) => columnDefs[field]);
};

export const getColumnDefsEdit = (
  rowData: ContentGenerationRecord[],
  pageSize: number,
  currentPage: number,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void
): (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] => {
  const columnDefs = createColumnDefinitions(
    rowData,
    pageSize,
    currentPage,
    undefined,
    selectedItems,
    toggleItem,
    setSelectedItems,
    deselectAll,
    onEnableFinalReview
  );

  const fields = [
    ColumnField.CHECKBOX,
    ColumnField.PRODUCT_INFO,
    ColumnField.RETAILER_SKU,
    ColumnField.UPDATED_AT,
    ColumnField.VERSION,
    ColumnField.STATUS
  ];

  return fields.map((field) => columnDefs[field]);
};

export const getColumnDefsReview = (
  rowData: ContentGenerationRecord[],
  pageSize: number,
  currentPage: number,
  selectedItems?: Set<string>,
  toggleItem?: (retailerSku: string) => void,
  setSelectedItems?: (items: Set<string>) => void,
  deselectAll?: () => void,
  onEnableFinalReview?: () => void
): (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] => {
  const columnDefs = createColumnDefinitions(
    rowData,
    pageSize,
    currentPage,
    undefined,
    selectedItems,
    toggleItem,
    setSelectedItems,
    deselectAll,
    onEnableFinalReview
  );

  const fields = [
    ColumnField.PRODUCT_INFO,
    ColumnField.RETAILER_SKU,
    ColumnField.UPDATED_AT,
    ColumnField.VERSION,
    ColumnField.STATUS
  ];

  return fields.map((field) => columnDefs[field]);
};

export { getInitialProductData };
